import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CarIcon, BikeIcon, TruckIcon, AlertTriangleIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, PhoneIcon, BoxIcon } from 'lucide-react';
interface FormData {
  accidentType: string;
  injured: string;
  medicalTreatment: string;
  accidentDate: string;
  atFault: string;
  hasLawyer: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}
const initialFormData: FormData = {
  accidentType: '',
  injured: '',
  medicalTreatment: '',
  accidentDate: '',
  atFault: '',
  hasLawyer: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: ''
};
const TOTAL_STEPS = 7;
export function MultiStepForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualificationReason, setDisqualificationReason] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const goNext = () => {
    setDirection(1);
    setCurrentStep(prev => prev + 1);
  };
  const goBack = () => {
    setDirection(-1);
    setCurrentStep(prev => prev - 1);
  };
  const goToStart = () => {
    setDirection(-1);
    setCurrentStep(1);
    setIsDisqualified(false);
    setDisqualificationReason('');
    setFormData(initialFormData);
  };
  const handleSelect = (field: keyof FormData, value: string, autoAdvance = true) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Conditional Logic Checks
    if (field === 'injured' && value === 'No') {
      setIsDisqualified(true);
      setDisqualificationReason('injury');
      return;
    }
    if (field === 'accidentDate' && value === 'Más de 6 meses') {
      setIsDisqualified(true);
      setDisqualificationReason('time');
      return;
    }
    if (field === 'atFault' && value === 'Sí') {
      setIsDisqualified(true);
      setDisqualificationReason('fault');
      return;
    }
    if (field === 'hasLawyer' && value === 'Sí') {
      setIsDisqualified(true);
      setDisqualificationReason('lawyer');
      return;
    }
    if (autoAdvance) {
      setTimeout(() => {
        goNext();
      }, 300);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('firstName', formData.firstName);
      params.append('lastName', formData.lastName);
      params.append('phone', formData.phone);
      params.append('email', formData.email);
      params.append('accidentType', formData.accidentType);
      params.append('injured', formData.injured);
      params.append('medicalTreatment', formData.medicalTreatment);
      params.append('accidentDate', formData.accidentDate);
      params.append('atFault', formData.atFault);
      params.append('hasLawyer', formData.hasLawyer);
      await fetch('https://services.leadconnectorhq.com/hooks/3hVnR8h224OAFaOU1YLc/webhook-trigger/52f70fa2-cdda-4f7c-977c-dd5fb54e8a6b', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params.toString(),
        mode: 'no-cors'
      });
    } catch (error) {
      console.error('Webhook error:', error);
    }
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: formData.accidentType
      });
    }
    setIsSubmitting(false);
    setIsSubmitted(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const renderDisqualification = () => {
    let message = '';
    if (disqualificationReason === 'fault') {
      message = 'Lo sentimos, en este momento no podemos ayudarle. Solo representamos a personas que no tuvieron la culpa del accidente. Si tiene preguntas, puede llamarnos al (786) 285-2768.';
    } else if (disqualificationReason === 'lawyer') {
      message = 'Parece que ya tiene representación legal. Si desea una segunda opinión, llámenos al (786) 285-2768.';
    } else if (disqualificationReason === 'injury') {
      message = 'Lo sentimos, solo podemos representar casos con lesiones. Nos alegra que esté bien. Si tiene dudas, llámenos al (786) 285-2768.';
    } else if (disqualificationReason === 'time') {
      message = 'Lo sentimos, el plazo para presentar una reclamación puede haber expirado. Para verificar su situación específica, llámenos al (786) 285-2768.';
    }
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-orange-light border border-orange/30 rounded-2xl p-8 text-center max-w-2xl mx-auto shadow-xl" data-id="element-45">
        <AlertTriangleIcon className="w-16 h-16 text-orange mx-auto mb-6" data-id="element-46" />
        <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-47">Aviso Importante</h3>
        <p className="text-lg text-gray-700 mb-8" data-id="element-48">{message}</p>

        <a href="tel:7862852768" onClick={() => {
        if (window.fbq) window.fbq('track', 'Contact');
      }} className="inline-flex items-center justify-center gap-2 bg-green hover:bg-green-hover text-white font-bold py-4 px-8 rounded-xl w-full md:w-auto mb-4 transition-colors text-lg" data-id="element-49">
          <PhoneIcon className="w-5 h-5 fill-current" data-id="element-50" />
          (786) 285-2768
        </a>

        <button onClick={goToStart} className="block w-full text-orange font-semibold hover:text-orange-hover transition-colors mt-4" data-id="element-51">
          Volver al inicio
        </button>
      </motion.div>;
  };
  const renderSuccess = () => <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="bg-white rounded-2xl p-8 md:p-12 text-center max-w-2xl mx-auto shadow-2xl" data-id="element-52">
      <CheckCircleIcon className="w-20 h-20 text-green mx-auto mb-6" data-id="element-53" />
      <h2 className="text-3xl font-black text-navy mb-4" data-id="element-54">
        ¡Gracias! Hemos recibido su información.
      </h2>
      <p className="text-lg text-gray-600 mb-8" data-id="element-55">
        Un abogado de Founders Law se comunicará con usted en breve. Recuerde:
        no paga nada a menos que ganemos su caso.
      </p>
      <a href="tel:7862852768" onClick={() => {
      if (window.fbq) window.fbq('track', 'Contact');
    }} className="inline-flex items-center justify-center gap-3 bg-green hover:bg-green-hover text-white font-black py-5 px-10 rounded-xl w-full md:w-auto transition-colors text-xl shadow-lg hover:shadow-green/20" data-id="element-56">
        <PhoneIcon className="w-6 h-6 fill-current" data-id="element-57" />
        LLAME AHORA: (786) 285-2768
      </a>
    </motion.div>;
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-3" data-id="element-58">
            <h2 className="text-xl md:text-2xl font-bold text-navy text-center mb-4" data-id="element-59">
              ELIJA EL TIPO DE ACCIDENTE:
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-60">
              {[{
              id: 'auto',
              icon: CarIcon,
              label: 'Accidente de Auto'
            }, {
              id: 'moto',
              icon: BikeIcon,
              label: 'Accidente de Moto'
            }, {
              id: 'camion',
              icon: TruckIcon,
              label: 'Accidente de Camión/18 Ruedas'
            }, {
              id: 'uber',
              icon: CarIcon,
              label: 'Uber/Lyft/Rideshare'
            }, {
              id: 'resbalon',
              icon: AlertTriangleIcon,
              label: 'Resbalón y Caída'
            }, {
              id: 'otro',
              icon: BoxIcon,
              label: 'Otro'
            }].map(item => {
              const isSelected = formData.accidentType === item.label;
              return <button key={item.id} onClick={() => handleSelect('accidentType', item.label)} className={`flex flex-col items-center justify-center text-center p-3 rounded-xl border-2 transition-all duration-200 ${isSelected ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-600 hover:text-orange bg-white'}`} data-id="element-61">
                    <item.icon className={`w-8 h-8 mb-2 ${isSelected ? 'text-orange' : 'text-gray-400'}`} data-id="element-62" />
                    <span className="text-xs font-semibold leading-tight" data-id="element-63">
                      {item.label}
                    </span>
                  </button>;
            })}
            </div>
          </div>;
      case 2:
        return <div className="space-y-3" data-id="element-64">
            <div className="text-center mb-4" data-id="element-65">
              <h2 className="text-xl md:text-2xl font-bold text-navy mb-1" data-id="element-66">
                ¿Resultó herido en el accidente?
              </h2>
              <p className="text-gray-500 font-medium text-sm" data-id="element-67">
                (Si no está seguro, seleccione Sí.)
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg mx-auto" data-id="element-68">
              {['Sí', 'No'].map(option => <button key={option} onClick={() => handleSelect('injured', option)} className={`py-4 px-6 rounded-xl border-2 text-lg font-bold transition-all duration-200 ${formData.injured === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-69">
                  {option}
                </button>)}
            </div>
          </div>;
      case 3:
        return <div className="space-y-3" data-id="element-70">
            <h2 className="text-xl md:text-2xl font-bold text-navy text-center mb-4" data-id="element-71">
              ¿Recibió tratamiento médico?
            </h2>
            <div className="grid grid-cols-1 gap-3 max-w-lg mx-auto" data-id="element-72">
              {['Sí', 'No', 'Todavía no, pero lo necesito'].map(option => <button key={option} onClick={() => handleSelect('medicalTreatment', option)} className={`py-4 px-6 rounded-xl border-2 text-lg font-bold transition-all duration-200 ${formData.medicalTreatment === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-73">
                  {option}
                </button>)}
            </div>
          </div>;
      case 4:
        return <div className="space-y-3" data-id="element-74">
            <h2 className="text-xl md:text-2xl font-bold text-navy text-center mb-4" data-id="element-75">
              ¿Cuándo ocurrió el accidente?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg mx-auto" data-id="element-76">
              {['Menos de 7 días', 'Menos de 1 mes', '1 a 6 meses', 'Más de 6 meses'].map(option => <button key={option} onClick={() => handleSelect('accidentDate', option)} className={`py-3 px-4 rounded-xl border-2 text-base font-bold transition-all duration-200 ${formData.accidentDate === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-77">
                  {option}
                </button>)}
            </div>
          </div>;
      case 5:
        return <div className="space-y-3" data-id="element-78">
            <h2 className="text-xl md:text-2xl font-bold text-navy text-center mb-4" data-id="element-79">
              ¿Fue usted el culpable del accidente?
            </h2>
            <div className="grid grid-cols-1 gap-3 max-w-lg mx-auto" data-id="element-80">
              {['Sí', 'No', 'No estoy seguro'].map(option => <button key={option} onClick={() => handleSelect('atFault', option)} className={`py-4 px-6 rounded-xl border-2 text-lg font-bold transition-all duration-200 ${formData.atFault === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-81">
                  {option}
                </button>)}
            </div>
          </div>;
      case 6:
        return <div className="space-y-3" data-id="element-82">
            <div className="text-center mb-4" data-id="element-83">
              <h2 className="text-xl md:text-2xl font-bold text-navy mb-1" data-id="element-84">
                ¿Ya tiene un abogado para su caso?
              </h2>
              <p className="text-gray-500 font-medium text-sm" data-id="element-85">
                (Si no tiene contrato firmado, seleccione No.)
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg mx-auto" data-id="element-86">
              {['Sí', 'No'].map(option => <button key={option} onClick={() => handleSelect('hasLawyer', option)} className={`py-4 px-6 rounded-xl border-2 text-lg font-bold transition-all duration-200 ${formData.hasLawyer === option ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-orange text-gray-700 bg-white'}`} data-id="element-87">
                  {option}
                </button>)}
            </div>
          </div>;
      case 7:
        return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-88">
            <div className="text-center mb-8" data-id="element-89">
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-2" data-id="element-90">
                ¡Último paso!
              </h2>
              <p className="text-gray-600 font-medium" data-id="element-91">
                ¿Cómo nos ponemos en contacto con usted?
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-92">
              <div data-id="element-93">
                <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-94">
                  Nombre
                </label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange focus:ring-0 outline-none transition-colors" data-id="element-95" />
              </div>
              <div data-id="element-96">
                <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-97">
                  Apellido
                </label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange focus:ring-0 outline-none transition-colors" data-id="element-98" />
              </div>
              <div data-id="element-99">
                <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-100">
                  Teléfono
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange focus:ring-0 outline-none transition-colors" data-id="element-101" />
              </div>
              <div data-id="element-102">
                <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-103">
                  Correo electrónico
                </label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-orange focus:ring-0 outline-none transition-colors" data-id="element-104" />
              </div>
            </div>

            <button type="submit" className="w-full bg-orange hover:bg-orange-hover text-white font-black py-4 rounded-xl text-lg md:text-xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] shadow-lg" data-id="element-105">
              {isSubmitting ? 'ENVIANDO...' : 'ENVIAR MI CASO GRATIS'}{' '}
              {!isSubmitting && <ChevronRightIcon className="w-6 h-6" data-id="element-106" />}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4 px-4 leading-relaxed" data-id="element-107">
              Al hacer clic en Enviar, usted consiente expresamente recibir
              llamadas y mensajes de texto de Founders Law mediante tecnología
              automatizada. El consentimiento no es condición de servicio.
              Acepta nuestra Política de Privacidad y Términos.
            </p>
          </form>;
      default:
        return null;
    }
  };
  if (isSubmitted) {
    return <div className="px-4 pb-20 md:pb-12 -mt-8 relative z-10" data-id="element-108">
        {renderSuccess()}
      </div>;
  }
  if (isDisqualified) {
    return <div className="px-4 pb-20 md:pb-12 -mt-8 relative z-10" data-id="element-109">
        {renderDisqualification()}
      </div>;
  }
  return <div className="px-4 pb-24 md:pb-16 mt-2 relative z-10" data-id="element-110">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl mx-auto overflow-hidden" data-id="element-111">
        {/* Progress Header */}
        {currentStep <= TOTAL_STEPS && <div className="bg-gray-50 border-b border-gray-100 px-4 py-2.5 md:px-6 md:py-3 flex flex-col items-center" data-id="element-112">
            <span className="text-xs font-bold text-gray-400 tracking-widest mb-1.5" data-id="element-113">
              PASO {currentStep} DE {TOTAL_STEPS}
            </span>
            <div className="flex gap-1 md:gap-1.5" data-id="element-114">
              {[...Array(TOTAL_STEPS)].map((_, i) => <div key={i} className={`h-2 rounded-full transition-all duration-300 ${i + 1 <= currentStep ? 'w-6 md:w-8 bg-orange' : 'w-2 md:w-3 bg-gray-200'}`} data-id="element-115" />)}
            </div>
          </div>}

        {/* Form Content Area */}
        <div className="p-4 md:p-6 relative min-h-[280px] flex flex-col" data-id="element-116">
          {currentStep > 1 && <button onClick={goBack} className="absolute top-4 left-4 text-gray-400 hover:text-orange flex items-center gap-1 font-semibold text-xs transition-colors z-20" data-id="element-117">
              <ChevronLeftIcon className="w-3.5 h-3.5" data-id="element-118" /> Anterior
            </button>}

          <div className="flex-1 flex flex-col justify-center mt-5 md:mt-2 relative overflow-hidden" data-id="element-119">
            <AnimatePresence mode="wait" custom={direction} data-id="element-120">
              <motion.div key={currentStep} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} className="w-full" data-id="element-121">
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}